/**
 * WeaveIn.ai Platform - Document Processing API Service
 * TypeScript service layer for doula document-to-claims pipeline
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
const API_VERSION = '1.0';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type QueueItemStatus =
  | 'pending'
  | 'processing'
  | 'extracted'
  | 'review'
  | 'approved'
  | 'claim_created'
  | 'failed'
  | 'rejected';

export interface ExtractedField {
  value: string | null;
  confidence: number;
  source_key?: string;
  field_type: string;
}

export interface MemberAlternative {
  id: number;
  name: string;
  similarity: number;
}

export interface ExtractionResult {
  id: number;
  queue_item_id: number;
  extracted_fields: Record<string, ExtractedField>;
  field_confidences: Record<string, number>;
  overall_confidence: number;
  total_fields: number;
  high_confidence_fields: number;
  medium_confidence_fields: number;
  low_confidence_fields: number;
  corrections: Record<string, any>;
  approved_fields: Record<string, any>;
  suggested_claim_data: SuggestedClaimData;
  created_at: string;
  updated_at: string;
}

export interface SuggestedClaimData {
  organization_id: number;
  member_id: number | null;
  service_date_from: string | null;
  service_date_to: string | null;
  place_of_service: string;
  diagnosis_code_1: string | null;
  rendering_provider_name: string | null;
  rendering_provider_npi: string | null;
  internal_notes: string;
  line_items: SuggestedLineItem[];
}

export interface SuggestedLineItem {
  line_number: number;
  billing_activity_id?: number;
  cpt_code: string;
  modifier_1?: string;
  service_date: string | null;
  units: number;
  charge_amount: number;
  service_description: string;
  activity_name?: string;
  rate_per_unit?: number;
}

export interface ReviewAction {
  id: number;
  queue_item_id: number;
  action_type: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  rejection_reason?: string;
  notes?: string;
  performed_by: number;
  performer_email?: string;
  performed_at: string;
}

export interface QueueItem {
  id: number;
  organization_id: number;
  source_type: string;
  source_file_id?: string;
  source_file_name?: string;
  source_file_path?: string;
  media_asset_id?: number;
  storage_path?: string;
  storage_url?: string;
  file_size?: number;
  mime_type?: string;
  status: QueueItemStatus;
  status_message?: string;
  ocr_model_id?: string;
  ocr_started_at?: string;
  ocr_completed_at?: string;
  ocr_duration_ms?: number;
  overall_confidence: number;
  fields_extracted: number;
  fields_requiring_review: number;
  matched_member_id?: number;
  member_match_confidence?: number;
  member_match_method?: string;
  claim_id?: number;
  program_id?: number;
  document_type: string;
  page_count?: number;
  metadata?: Record<string, any>;
  created_by?: number;
  reviewed_by?: number;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  member_name?: string;
  claim_number?: string;
  created_by_email?: string;
  // Detail-only fields
  extraction_result?: ExtractionResult;
  review_history?: ReviewAction[];
}

export interface FieldMapping {
  form_field: string;
  claim_field: string;
  field_type: string;
  is_required: boolean;
  default_value?: string;
  transformation?: string;
  ocr_field_hint?: string;
}

export interface CptMappingRule {
  visit_type_keyword: string;
  cpt_code: string;
  modifiers: string[];
  default_units: number;
  billing_activity_id?: number;
  duration_range?: [number, number];
}

export interface DocumentFieldMappingConfig {
  id: number | null;
  organization_id: number;
  document_type: string;
  form_name?: string;
  form_version?: string;
  field_mappings: FieldMapping[];
  cpt_mapping_rules: CptMappingRule[];
  default_place_of_service: string;
  default_diagnosis_code?: string;
  is_active: boolean;
}

export interface SharePointConfig {
  id: number | null;
  organization_id: number;
  tenant_id?: string;
  site_id?: string;
  drive_id?: string;
  folder_path: string;
  polling_interval_minutes: number;
  is_active: boolean;
  last_sync_at?: string;
  last_sync_status?: string;
  last_sync_error?: string;
  last_sync_file_count?: number;
  move_processed_to?: string;
  credentials_reference?: string;
}

export interface ReviewSubmission {
  action: 'approve' | 'reject' | 'correct_field';
  corrections?: Record<string, any>;
  rejection_reason?: string;
  notes?: string;
}

export interface ProcessingStats {
  total_documents: number;
  pending: number;
  processing: number;
  extracted: number;
  in_review: number;
  approved: number;
  claims_created: number;
  failed: number;
  rejected: number;
  avg_confidence: number;
  avg_processing_time_ms: number;
}

export interface QueueFilters {
  status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  document_type?: string;
  limit?: number;
  offset?: number;
}

export interface QueueListResponse {
  items: QueueItem[];
  total: number;
}

export interface ClaimCreateResult {
  success: boolean;
  claim_id?: number;
  claim_number?: string;
  queue_item_id?: number;
  error?: string;
}

export interface SyncResult {
  success: boolean;
  files_found: number;
  files_queued: number;
}

// ============================================================================
// INSURANCE CARD & MASTER CLAIM TYPES
// ============================================================================

export type InsuranceDocStatus = 'pending' | 'extracted' | 'matched' | 'profile_created';

export interface InsuranceDocument {
  id: number;
  organization_id: number;
  member_id: number | null;
  document_category: string;
  card_type: string | null;
  queue_item_id: number;
  member_cin: string | null;
  member_plan_id: string | null;
  plan_name: string | null;
  extracted_first_name: string | null;
  extracted_last_name: string | null;
  extracted_dob: string | null;
  match_confidence: number | null;
  match_method: string | null;
  processing_status: InsuranceDocStatus;
  coverage_effective_date: string | null;
  language: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  member_name?: string;
  source_file_name?: string;
  storage_url?: string;
  mime_type?: string;
  extraction_result?: ExtractionResult;
}

export interface InsuranceDocFilters {
  status?: string;
  member_id?: number;
  document_category?: string;
  limit?: number;
  offset?: number;
}

export interface InsuranceDocListResponse {
  items: InsuranceDocument[];
  total: number;
}

export interface MemberMatchRequest {
  member_id?: number;
  create_new?: {
    first_name: string;
    last_name: string;
    date_of_birth?: string;
    medicaid_id?: string;
  };
}

// Backend returns the updated InsuranceDocument dict after match/create
export interface MemberMatchResult {
  id: number;
  member_id: number;
  member_name?: string;
  match_method?: string;
  processing_status: string;
  [key: string]: any;
}

export type MasterClaimStatus = 'draft' | 'ready' | 'approved' | 'submitted' | 'claim_created';

export interface MasterClaimLineItem {
  id: number;
  master_claim_id: number;
  line_type: string;
  service_date: string;
  delivery_type: string | null;
  suggested_cpt_code: string | null;
  line_number: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface MasterClaim {
  id: number;
  organization_id: number;
  program_id: number | null;
  member_id: number;
  due_date: string | null;
  health_plan: string | null;
  has_insurance_documents: boolean;
  insurance_verified: boolean;
  status: MasterClaimStatus;
  total_visits: number;
  total_deliveries: number;
  claim_id: number | null;
  internal_notes: string | null;
  flagged: boolean;
  flag_reason: string | null;
  submitted_on_portal: boolean;
  portal_submission_date: string | null;
  source_queue_item_id: number | null;
  created_by: number | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  member_name?: string;
  member_cin?: string;
  member_plan_id?: string;
  line_items?: MasterClaimLineItem[];
  insurance_documents?: InsuranceDocument[];
  claim_number?: string;
}

export interface MasterClaimCreate {
  member_id: number;
  program_id?: number;
  due_date?: string;
  health_plan?: string;
  source_queue_item_id?: number;
  internal_notes?: string;
}

export interface MasterClaimUpdate {
  due_date?: string;
  health_plan?: string;
  internal_notes?: string;
  status?: string;
  submitted_on_portal?: boolean;
  portal_submission_date?: string;
}

export interface MasterClaimLineItemCreate {
  line_type: string;
  service_date: string;
  delivery_type?: string;
  notes?: string;
}

export interface MasterClaimFilters {
  status?: string;
  member_id?: number;
  flagged?: boolean;
  limit?: number;
  offset?: number;
}

export interface ScanExtractedFields {
  date_of_service: string | null;
  service_type: string | null;
  due_date: string | null;
  health_plan: string | null;
  member_cin: string | null;
  member_plan_id: string | null;
  plan_name: string | null;
  doula_name: string | null;
  duration_minutes: string | null;
}

export interface MasterClaimUploadResult {
  queue_item_id: number;
  member_id: number | null;
  member_name: string | null;
  match_method: string | null;
  match_confidence: number | null;
  extracted_fields: ScanExtractedFields;
  message?: string;
}

export interface MasterClaimListResponse {
  items: MasterClaim[];
  total: number;
}

export interface ConvertToClaimResult {
  success: boolean;
  claim_id?: number;
  claim_number?: string;
  error?: string;
}

export interface InsuranceDocFieldUpdate {
  member_cin?: string;
  member_plan_id?: string;
  plan_name?: string;
  extracted_first_name?: string;
  extracted_last_name?: string;
  extracted_dob?: string;
  coverage_effective_date?: string;
  language?: string;
}

export interface CardTemplate {
  id: number;
  organization_id: number | null;
  card_type: string;
  plan_name: string;
  plan_variants: string[];
  regex_patterns: Record<string, string>;
  expected_fields: string[];
  times_scanned: number;
  successful_extractions: number;
  avg_confidence: number;
  is_active: boolean;
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// API SERVICE CLASS
// ============================================================================

class DocumentProcessingApiService {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('[DocumentProcessing API] Unauthorized - token may be expired');
        }
        return Promise.reject(error);
      }
    );
  }

  // ---- Queue ----

  async listQueue(filters?: QueueFilters): Promise<QueueListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.document_type) params.append('document_type', filters.document_type);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    const resp = await this.client.get<QueueListResponse>(
      `/api/document-processing/queue?${params.toString()}`
    );
    return resp.data;
  }

  async getQueueItem(id: number): Promise<QueueItem> {
    const resp = await this.client.get<QueueItem>(`/api/document-processing/queue/${id}`);
    return resp.data;
  }

  async deleteQueueItem(id: number): Promise<{ success: boolean; message: string }> {
    const resp = await this.client.delete<{ success: boolean; message: string }>(
      `/api/document-processing/queue/${id}`
    );
    return resp.data;
  }

  // ---- Upload & Processing ----

  async uploadDocument(file: File, programId?: number): Promise<QueueItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (programId) formData.append('program_id', String(programId));
    const resp = await this.client.post<QueueItem>('/api/document-processing/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  }

  async processDocument(id: number): Promise<any> {
    const resp = await this.client.post(`/api/document-processing/queue/${id}/process`);
    return resp.data;
  }

  async reprocessDocument(id: number): Promise<any> {
    const resp = await this.client.post(`/api/document-processing/queue/${id}/reprocess`);
    return resp.data;
  }

  // ---- Review ----

  async submitReview(id: number, review: ReviewSubmission): Promise<QueueItem> {
    const resp = await this.client.put<QueueItem>(
      `/api/document-processing/queue/${id}/review`,
      review
    );
    return resp.data;
  }

  // ---- Claim Creation ----

  async createClaimFromExtraction(
    id: number,
    corrections?: Record<string, any>
  ): Promise<ClaimCreateResult> {
    const resp = await this.client.post<ClaimCreateResult>(
      `/api/document-processing/queue/${id}/create-claim`,
      { corrections }
    );
    return resp.data;
  }

  // ---- Field Mappings ----

  async getFieldMappings(documentType: string = 'doula_visit_form'): Promise<DocumentFieldMappingConfig> {
    const resp = await this.client.get<DocumentFieldMappingConfig>(
      '/api/document-processing/field-mappings',
      { params: { document_type: documentType } }
    );
    return resp.data;
  }

  async updateFieldMappings(
    config: Partial<DocumentFieldMappingConfig>
  ): Promise<DocumentFieldMappingConfig> {
    const resp = await this.client.put<DocumentFieldMappingConfig>(
      '/api/document-processing/field-mappings',
      config
    );
    return resp.data;
  }

  // ---- SharePoint Config ----

  async getSharePointConfig(): Promise<SharePointConfig> {
    const resp = await this.client.get<SharePointConfig>(
      '/api/document-processing/sharepoint-config'
    );
    return resp.data;
  }

  async updateSharePointConfig(config: Partial<SharePointConfig>): Promise<SharePointConfig> {
    const resp = await this.client.put<SharePointConfig>(
      '/api/document-processing/sharepoint-config',
      config
    );
    return resp.data;
  }

  async triggerSync(): Promise<SyncResult> {
    const resp = await this.client.post<SyncResult>('/api/document-processing/sync');
    return resp.data;
  }

  // ---- Stats ----

  async getStats(document_type?: string): Promise<ProcessingStats> {
    const params = document_type ? `?document_type=${encodeURIComponent(document_type)}` : '';
    const resp = await this.client.get<ProcessingStats>(`/api/document-processing/stats${params}`);
    return resp.data;
  }

  // ---- Insurance Cards ----

  async uploadInsuranceCard(
    file: File,
    cardType: string,
    documentCategory: string = 'health_plan_card'
  ): Promise<InsuranceDocument> {
    const formData = new FormData();
    formData.append('file', file);
    // An empty card type means "detect the plan from the card" (TRUS-725).
    if (cardType) formData.append('card_type', cardType);
    formData.append('document_category', documentCategory);
    const resp = await this.client.post<InsuranceDocument>(
      '/api/document-processing/insurance-cards/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return resp.data;
  }

  async listInsuranceCards(filters?: InsuranceDocFilters): Promise<InsuranceDocListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.member_id) params.append('member_id', String(filters.member_id));
    if (filters?.document_category) params.append('document_category', filters.document_category);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    const resp = await this.client.get<InsuranceDocListResponse>(
      `/api/document-processing/insurance-cards?${params.toString()}`
    );
    return resp.data;
  }

  async getInsuranceCard(id: number): Promise<InsuranceDocument> {
    const resp = await this.client.get<InsuranceDocument>(
      `/api/document-processing/insurance-cards/${id}`
    );
    return resp.data;
  }

  async processInsuranceCard(id: number): Promise<InsuranceDocument> {
    const resp = await this.client.post<InsuranceDocument>(
      `/api/document-processing/insurance-cards/${id}/process`
    );
    return resp.data;
  }

  async matchMemberToCard(id: number, request: MemberMatchRequest): Promise<MemberMatchResult> {
    const resp = await this.client.put<MemberMatchResult>(
      `/api/document-processing/insurance-cards/${id}/match-member`,
      request
    );
    return resp.data;
  }

  async deleteInsuranceCard(id: number): Promise<{ success: boolean; message: string }> {
    const resp = await this.client.delete<{ success: boolean; message: string }>(
      `/api/document-processing/insurance-cards/${id}`
    );
    return resp.data;
  }

  async updateInsuranceCardFields(id: number, fields: InsuranceDocFieldUpdate): Promise<InsuranceDocument> {
    const resp = await this.client.put<InsuranceDocument>(
      `/api/document-processing/insurance-cards/${id}/fields`,
      fields
    );
    return resp.data;
  }

  // ---- Card Templates ----

  async listCardTemplates(): Promise<CardTemplate[]> {
    const resp = await this.client.get<CardTemplate[]>('/api/document-processing/card-templates');
    return resp.data;
  }

  async createCardTemplate(data: { card_type: string; plan_name: string; plan_variants?: string[]; regex_patterns?: Record<string, string> }): Promise<{ id: number }> {
    const resp = await this.client.post<{ id: number }>('/api/document-processing/card-templates', data);
    return resp.data;
  }

  async updateCardTemplate(id: number, data: Partial<Pick<CardTemplate, 'plan_name' | 'plan_variants' | 'regex_patterns' | 'expected_fields' | 'is_active'>>): Promise<{ id: number; updated: boolean }> {
    const resp = await this.client.put<{ id: number; updated: boolean }>(`/api/document-processing/card-templates/${id}`, data);
    return resp.data;
  }

  // ---- Master Claims ----

  async createMasterClaim(data: MasterClaimCreate): Promise<MasterClaim> {
    const resp = await this.client.post<MasterClaim>(
      '/api/document-processing/master-claims',
      data
    );
    return resp.data;
  }

  async scanAndExtractMasterClaim(file: File): Promise<MasterClaimUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const resp = await this.client.post<MasterClaimUploadResult>(
      '/api/document-processing/master-claims/scan-and-extract',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
    return resp.data;
  }

  async listMasterClaims(filters?: MasterClaimFilters): Promise<MasterClaimListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.member_id) params.append('member_id', String(filters.member_id));
    if (filters?.flagged !== undefined) params.append('flagged', String(filters.flagged));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    const resp = await this.client.get<MasterClaimListResponse>(
      `/api/document-processing/master-claims?${params.toString()}`
    );
    return resp.data;
  }

  async getMasterClaim(id: number): Promise<MasterClaim> {
    const resp = await this.client.get<MasterClaim>(
      `/api/document-processing/master-claims/${id}`
    );
    return resp.data;
  }

  async updateMasterClaim(id: number, data: MasterClaimUpdate): Promise<MasterClaim> {
    const resp = await this.client.put<MasterClaim>(
      `/api/document-processing/master-claims/${id}`,
      data
    );
    return resp.data;
  }

  async deleteMasterClaim(id: number): Promise<{ success: boolean; message: string }> {
    const resp = await this.client.delete<{ success: boolean; message: string }>(
      `/api/document-processing/master-claims/${id}`
    );
    return resp.data;
  }

  async addMasterClaimLineItem(
    claimId: number,
    data: MasterClaimLineItemCreate
  ): Promise<MasterClaimLineItem> {
    const resp = await this.client.post<MasterClaimLineItem>(
      `/api/document-processing/master-claims/${claimId}/line-items`,
      data
    );
    return resp.data;
  }

  async updateMasterClaimLineItem(
    claimId: number,
    lineId: number,
    data: Partial<MasterClaimLineItemCreate>
  ): Promise<MasterClaimLineItem> {
    const resp = await this.client.put<MasterClaimLineItem>(
      `/api/document-processing/master-claims/${claimId}/line-items/${lineId}`,
      data
    );
    return resp.data;
  }

  async deleteMasterClaimLineItem(
    claimId: number,
    lineId: number
  ): Promise<{ success: boolean; message: string }> {
    const resp = await this.client.delete<{ success: boolean; message: string }>(
      `/api/document-processing/master-claims/${claimId}/line-items/${lineId}`
    );
    return resp.data;
  }

  async convertMasterClaimToClaim(id: number): Promise<ConvertToClaimResult> {
    const resp = await this.client.post<ConvertToClaimResult>(
      `/api/document-processing/master-claims/${id}/convert-to-claim`
    );
    return resp.data;
  }
}

export const documentProcessingApi = new DocumentProcessingApiService();
export default documentProcessingApi;
